<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { between } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const MIN_WAIT = 1;
const MAX_WAIT = 1440;

export default {
  components: {
    NextButton,
    SettingsToggleSection,
    MultiselectDropdown,
    MultiselectDropdownItems,
    TagInput,
    TagMultiSelectComboBox,
  },
  props: { inbox: { type: Object, default: () => ({}) } },
  setup() {
    return { v$: useVuelidate(), MIN_WAIT, MAX_WAIT };
  },
  data() {
    return {
      // General
      slack_alerts_enabled: false,
      slack_channel_id: '',
      slack_mention_codes: [],
      showMentionDropdown: false,
      slackChannels: [],
      priority_channels: {
        low: '',
        medium: '',
        high: '',
        urgent: '',
      },
      priority_wait_minutes: {
        low: 0,
        medium: 0,
        high: 0,
        urgent: 0,
      },
      // Keyword rule
      rule_keyword_enabled: true,
      rule_ai_prompt: '',
      rule_keyword_cooldown: 10,
      rule_keyword_list: [],
      showKeywordAdvanced: false,
      // Max outgoing rule
      rule_outgoing_enabled: true,
      rule_outgoing_threshold: 7,
      rule_outgoing_apply_ai: false,
      rule_outgoing_ai_prompt: '',
      rule_outgoing_repeat: false,
      rule_outgoing_skip_labels: [],
      showOutgoingAdvanced: false,
      // Max unresolved days
      rule_days_enabled: true,
      rule_days_threshold: 3,
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters({
      accountLabelsFromStore: 'labels/getLabels',
      accountAgents: 'agents/getAgents',
    }),
    accountLabelTitles() {
      return (this.accountLabelsFromStore || []).map(l => l.title);
    },
    labelMenuItems() {
      return this.accountLabelTitles.map(l => ({ value: l, label: l }));
    },
    agentsWithSlackCodes() {
      return (this.accountAgents || [])
        .filter(agent => agent.slack_mention_code)
        .map(agent => ({
          id: agent.id,
          name: agent.name,
          email: agent.email,
          thumbnail: agent.thumbnail,
          availability_status: agent.availability_status,
          slack_mention_code: agent.slack_mention_code,
        }));
    },
    selectedMentionAgents: {
      // Getter: Map slack_mention_codes array to agent objects for display
      get() {
        return this.agentsWithSlackCodes.filter(agent =>
          this.slack_mention_codes.includes(agent.slack_mention_code)
        );
      },
      // Setter: Extract slack_mention_codes from selected agent objects
      set(selectedAgents) {
        if (!selectedAgents) {
          this.slack_mention_codes = [];
          return;
        }

        // Handle both array and single object
        const agents = Array.isArray(selectedAgents)
          ? selectedAgents
          : [selectedAgents];

        // Extract mention codes from agent objects
        this.slack_mention_codes = agents.map(
          agent => agent.slack_mention_code
        );
      },
    },
    slackChannelOptions() {
      return this.slackChannels.map(ch => ({
        id: ch.id,
        name: `#${ch.name}`,
        value: String(ch.id),
      }));
    },
    selectedMainChannel() {
      if (!this.slack_channel_id) return {};
      const found = this.slackChannelOptions.find(
        opt => opt.value === this.slack_channel_id
      );
      return found || {};
    },
  },
  validations: {
    priority_wait_minutes: {
      low: { between: between(MIN_WAIT, MAX_WAIT) },
      medium: { between: between(MIN_WAIT, MAX_WAIT) },
      high: { between: between(MIN_WAIT, MAX_WAIT) },
      urgent: { between: between(MIN_WAIT, MAX_WAIT) },
    },
  },
  mounted() {
    this.$store.dispatch('agents/get').catch(() => {});
    this.setDefaults();
    this.fetchSlackChannels().catch(() => {});
  },
  methods: {
    onSelectMainChannel(channel) {
      this.slack_channel_id = channel.value || '';
    },
    onToggleMentionDropdown() {
      this.showMentionDropdown = !this.showMentionDropdown;
    },
    onCloseMentionDropdown() {
      this.showMentionDropdown = false;
    },
    onSelectMentionAgent(agent) {
      const isSelected = this.slack_mention_codes.includes(
        agent.slack_mention_code
      );

      if (isSelected) {
        this.slack_mention_codes = this.slack_mention_codes.filter(
          code => code !== agent.slack_mention_code
        );
      } else {
        this.slack_mention_codes = [
          ...this.slack_mention_codes,
          agent.slack_mention_code,
        ];
      }
    },
    onRemoveMentionAgent(agent) {
      this.slack_mention_codes = this.slack_mention_codes.filter(
        code => code !== agent.slack_mention_code
      );
    },
    onSelectPriorityChannel(priority, channel) {
      this.priority_channels[priority] = channel.value || '';
    },
    selectedPriorityChannel(priority) {
      const value = this.priority_channels[priority];
      if (!value) return {};
      const found = this.slackChannelOptions.find(opt => opt.value === value);
      return found || {};
    },
    normalizeKeywordList(list) {
      if (Array.isArray(list)) return list;
      if (list) {
        return String(list)
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
      }
      return [];
    },
    setDefaults() {
      const slack = this.inbox?.inbox_config?.slack_alerts || {};
      this.slack_alerts_enabled = !!slack.enabled;
      this.slack_channel_id = slack.slack_channel_id || '';
      this.slack_mention_codes = Array.isArray(slack.slack_mention_codes)
        ? slack.slack_mention_codes
        : [];

      const priorityChannels = slack.priority_channels || {};
      this.priority_channels = {
        low: priorityChannels.low || '',
        medium: priorityChannels.medium || '',
        high: priorityChannels.high || '',
        urgent: priorityChannels.urgent || '',
      };

      const priorityWait = slack.priority_wait_minutes || {};
      this.priority_wait_minutes = {
        low: Number(priorityWait.low ?? 0),
        medium: Number(priorityWait.medium ?? 0),
        high: Number(priorityWait.high ?? 0),
        urgent: Number(priorityWait.urgent ?? 0),
      };

      const rules = slack.rules || {};
      const kw = rules.keyword || {};
      this.rule_keyword_enabled = kw.enabled ?? true;
      this.rule_ai_prompt = kw.ai_prompt || '';
      this.rule_keyword_cooldown = Number(kw.cooldown_minutes ?? 10);
      this.rule_keyword_list = this.normalizeKeywordList(kw.list);

      const mo = rules.max_outgoing || {};
      this.rule_outgoing_enabled = mo.enabled ?? true;
      this.rule_outgoing_threshold = Number(mo.threshold ?? 7);
      this.rule_outgoing_apply_ai = !!mo.apply_ai;
      this.rule_outgoing_ai_prompt = mo.ai_prompt || '';
      this.rule_outgoing_repeat = !!mo.repeat;
      this.rule_outgoing_skip_labels = Array.isArray(mo.skip_label_titles)
        ? mo.skip_label_titles
        : [];

      const md = rules.max_days_unresolved || {};
      this.rule_days_enabled = md.enabled ?? true;
      this.rule_days_threshold = Number(md.threshold_days ?? 3);

      this.showKeywordAdvanced = !!this.rule_ai_prompt;
      this.showOutgoingAdvanced =
        this.rule_outgoing_apply_ai || !!this.rule_outgoing_ai_prompt;
    },

    async updateSlackAlerts() {
      if (this.slack_alerts_enabled) {
        this.v$.priority_wait_minutes.$touch();
        if (this.v$.priority_wait_minutes.$invalid) {
          useAlert(this.$t('INBOX_MGMT.SLACK_ALERTS.PRIORITY_WAIT_INVALID'));
          return;
        }
      }

      try {
        this.isSaving = true;
        // sanitize selected channels: convert empty string to null and ensure string ids
        const sanitizeMapping = obj =>
          Object.fromEntries(
            Object.entries(obj || {}).map(([k, v]) => [
              k,
              v === '' || v === null ? null : String(v),
            ])
          );
        const sanitizedPriorityChannels = sanitizeMapping(
          this.priority_channels
        );
        const sanitizedSlackChannelId =
          this.slack_channel_id === '' ? null : String(this.slack_channel_id);

        const payload = {
          id: this.inbox.id,
          formData: false,
          inbox_config: {
            slack_alerts: {
              enabled: this.slack_alerts_enabled,
              slack_channel_id: sanitizedSlackChannelId,
              slack_mention_codes: this.slack_mention_codes,
              priority_channels: sanitizedPriorityChannels,
              priority_wait_minutes: this.priority_wait_minutes,
              rules: {
                keyword: {
                  ai_prompt: this.rule_ai_prompt,
                  enabled: this.rule_keyword_enabled,
                  cooldown_minutes: Number(this.rule_keyword_cooldown),
                  list: this.rule_keyword_list,
                },
                max_outgoing: {
                  enabled: this.rule_outgoing_enabled,
                  threshold: Number(this.rule_outgoing_threshold),
                  apply_ai: this.rule_outgoing_apply_ai,
                  ai_prompt: this.rule_outgoing_ai_prompt,
                  repeat: this.rule_outgoing_repeat,
                  skip_label_titles: this.rule_outgoing_skip_labels,
                },
                max_days_unresolved: {
                  enabled: this.rule_days_enabled,
                  threshold_days: Number(this.rule_days_threshold),
                },
              },
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (e) {
        useAlert(e.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isSaving = false;
      }
    },
    async fetchSlackChannels() {
      try {
        const channels = await this.$store.dispatch(
          'integrations/listAllSlackChannels'
        );
        this.slackChannels = Array.isArray(channels)
          ? channels.slice().sort((a, b) => {
              const an = (a.name || '').toString().toLowerCase();
              const bn = (b.name || '').toString().toLowerCase();
              return an.localeCompare(bn);
            })
          : [];
      } catch (e) {
        this.slackChannels = [];
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- GENERAL ENABLE -->
    <SettingsToggleSection
      v-model="slack_alerts_enabled"
      :header="$t('INBOX_MGMT.SLACK_ALERTS.ENABLE')"
      :description="$t('INBOX_MGMT.SLACK_ALERTS.GENERAL_DESC')"
    >
      <template v-if="slack_alerts_enabled" #editor>
        <div class="flex flex-col gap-4">
          <!-- Default channel -->
          <div>
            <p class="text-sm font-medium text-n-slate-12 pb-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.CHANNEL_NAME') }}
            </p>
            <MultiselectDropdown
              :options="slackChannelOptions"
              :selected-item="selectedMainChannel"
              :multiselect-placeholder="
                $t('INBOX_MGMT.SLACK_ALERTS.SLACK_CHANNEL_PLACEHOLDER')
              "
              :input-placeholder="$t('INBOX_MGMT.SLACK_ALERTS.SEARCH_CHANNELS')"
              :no-search-result="
                $t('INBOX_MGMT.SLACK_ALERTS.NO_CHANNELS_FOUND')
              "
              :has-thumbnail="false"
              @select="onSelectMainChannel"
            />
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.DEFAULT_CHANNEL_HELP') }}
            </p>
          </div>

          <!-- Mention agents -->
          <div>
            <p class="text-sm font-medium text-n-slate-12 pb-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.MENTION_AGENTS') }}
            </p>
            <div
              v-if="selectedMentionAgents.length > 0"
              class="flex flex-wrap gap-2 mb-2 p-2 border border-n-weak rounded-lg bg-n-solid-1"
            >
              <div
                v-for="agent in selectedMentionAgents"
                :key="agent.id"
                class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-n-alpha-2 hover:bg-n-alpha-3 transition-colors"
              >
                <span class="text-sm text-n-slate-12">{{ agent.name }}</span>
                <span class="text-xs text-n-slate-10">{{
                  agent.slack_mention_code
                }}</span>
                <button
                  type="button"
                  class="flex-shrink-0 cursor-pointer i-lucide-x size-3.5 text-n-slate-11 hover:text-n-slate-12"
                  @click.stop="onRemoveMentionAgent(agent)"
                />
              </div>
            </div>
            <div class="relative">
              <NextButton
                slate
                icon="i-lucide-users"
                trailing-icon
                class="w-full justify-between"
                @click="onToggleMentionDropdown"
              >
                <span class="text-sm">
                  {{
                    selectedMentionAgents.length === 0
                      ? $t('INBOX_MGMT.SLACK_ALERTS.SELECT_AGENTS_PLACEHOLDER')
                      : $t('INBOX_MGMT.SLACK_ALERTS.ADD_MORE_AGENTS')
                  }}
                </span>
              </NextButton>
              <div
                v-if="showMentionDropdown"
                v-on-clickaway="onCloseMentionDropdown"
                class="border rounded-lg shadow-lg bg-n-alpha-3 absolute backdrop-blur-[100px] border-n-strong dark:border-n-strong p-2 z-[9999] box-border top-12 w-full"
              >
                <div class="flex items-center justify-between mb-1">
                  <h4
                    class="m-0 overflow-hidden text-sm whitespace-nowrap text-ellipsis text-n-slate-12"
                  >
                    {{ $t('INBOX_MGMT.SLACK_ALERTS.MENTION_AGENTS') }}
                  </h4>
                  <NextButton
                    ghost
                    slate
                    xs
                    icon="i-lucide-x"
                    @click="onCloseMentionDropdown"
                  />
                </div>
                <MultiselectDropdownItems
                  :options="agentsWithSlackCodes"
                  :selected-items="selectedMentionAgents"
                  :input-placeholder="
                    $t('INBOX_MGMT.SLACK_ALERTS.SELECT_AGENTS_PLACEHOLDER')
                  "
                  :no-search-result="
                    $t('INBOX_MGMT.SLACK_ALERTS.NO_AGENTS_FOUND')
                  "
                  @select="onSelectMentionAgent"
                />
              </div>
            </div>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.MENTION_AGENTS_HELP') }}
            </p>
          </div>

          <!-- Priority channels -->
          <div>
            <p class="text-sm text-n-slate-11 pb-3">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.PRIORITY_CHANNELS_DESC') }}
            </p>
            <div class="space-y-3">
              <div
                v-for="priority in ['low', 'medium', 'high', 'urgent']"
                :key="priority"
                class="outline outline-1 -outline-offset-1 outline-n-weak rounded-xl overflow-hidden"
              >
                <div class="px-4 py-3 border-b border-n-weak">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'INBOX_MGMT.SLACK_ALERTS.PRIORITY_' +
                          priority.toUpperCase()
                      )
                    }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ $t('INBOX_MGMT.SLACK_ALERTS.PRIORITY_GROUP_DESC') }}
                  </div>
                </div>
                <div class="px-4 py-4">
                  <div
                    class="grid grid-cols-1 md:grid-cols-3 gap-4 items-start"
                  >
                    <div class="md:col-span-2">
                      <label class="block pb-1 text-sm text-n-slate-12">{{
                        $t('INBOX_MGMT.SLACK_ALERTS.CHANNEL_NAME')
                      }}</label>
                      <MultiselectDropdown
                        :options="slackChannelOptions"
                        :selected-item="selectedPriorityChannel(priority)"
                        :multiselect-placeholder="
                          $t('INBOX_MGMT.SLACK_ALERTS.CHANNEL_ID_PLACEHOLDER')
                        "
                        :input-placeholder="
                          $t('INBOX_MGMT.SLACK_ALERTS.SEARCH_CHANNELS')
                        "
                        :no-search-result="
                          $t('INBOX_MGMT.SLACK_ALERTS.NO_CHANNELS_FOUND')
                        "
                        :has-thumbnail="false"
                        @select="onSelectPriorityChannel(priority, $event)"
                      />
                    </div>
                    <div>
                      <div class="flex items-center justify-between mb-1">
                        <label class="text-sm text-n-slate-12">
                          {{
                            $t('INBOX_MGMT.SLACK_ALERTS.PRIORITY_WAIT_LABEL')
                          }}
                        </label>
                        <div
                          v-if="v$.priority_wait_minutes[priority]?.$error"
                          class="text-n-ruby-9"
                          role="img"
                        >
                          <i
                            v-tooltip.top="
                              $t(
                                'INBOX_MGMT.SLACK_ALERTS.PRIORITY_WAIT_INVALID'
                              )
                            "
                            class="i-lucide-alert-circle"
                            tabindex="0"
                          />
                        </div>
                      </div>
                      <woot-input
                        v-model="priority_wait_minutes[priority]"
                        type="number"
                        :min="MIN_WAIT"
                        :max="MAX_WAIT"
                        :step="1"
                        :placeholder="
                          $t(
                            'INBOX_MGMT.SLACK_ALERTS.PRIORITY_WAIT_PLACEHOLDER'
                          )
                        "
                        :class="{
                          error: v$.priority_wait_minutes[priority]?.$error,
                        }"
                        @blur="v$.priority_wait_minutes[priority].$touch()"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </SettingsToggleSection>

    <!-- KEYWORD RULE -->
    <SettingsToggleSection
      v-if="slack_alerts_enabled"
      v-model="rule_keyword_enabled"
      :header="$t('INBOX_MGMT.SLACK_ALERTS.RULE_KEYWORD')"
      :description="$t('INBOX_MGMT.SLACK_ALERTS.KEYWORD_DESC')"
    >
      <template v-if="rule_keyword_enabled" #editor>
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-4">
            <label class="flex-1 text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.COOLDOWN_MINUTES') }}
            </label>
            <input
              v-model="rule_keyword_cooldown"
              type="number"
              class="reset-base w-20 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong focus:outline-n-blue-9 px-3 py-1.5 text-sm bg-n-surface-1 text-n-slate-12"
            />
          </div>
          <div>
            <p class="text-sm text-n-slate-12 pb-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.KEYWORD_LIST_LABEL') }}
            </p>
            <TagInput
              v-model="rule_keyword_list"
              :placeholder="$t('INBOX_MGMT.SLACK_ALERTS.ADD_KEYWORD')"
              allow-create
              class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2 w-full"
            />
          </div>
          <details :open="!!rule_ai_prompt">
            <summary class="cursor-pointer text-sm text-n-slate-11">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.SHOW_ADVANCED') }}
            </summary>
            <div class="mt-3 flex flex-col gap-1">
              <p class="text-sm text-n-slate-12">
                {{ $t('INBOX_MGMT.SLACK_ALERTS.AI_PROMPT_LABEL') }}
              </p>
              <textarea
                v-model="rule_ai_prompt"
                rows="4"
                class="w-full rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong focus:outline-n-blue-9 p-3 text-sm bg-n-surface-1 text-n-slate-12 resize-none"
                :placeholder="
                  $t('INBOX_MGMT.SLACK_ALERTS.AI_PROMPT_PLACEHOLDER')
                "
                spellcheck="false"
              />
            </div>
          </details>
        </div>
      </template>
    </SettingsToggleSection>

    <!-- MAX OUTGOING -->
    <SettingsToggleSection
      v-if="slack_alerts_enabled"
      v-model="rule_outgoing_enabled"
      :header="$t('INBOX_MGMT.SLACK_ALERTS.RULE_MAX_OUTGOING')"
      :description="$t('INBOX_MGMT.SLACK_ALERTS.OUTGOING_DESC')"
    >
      <template v-if="rule_outgoing_enabled" #editor>
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-4">
            <label class="flex-1 text-sm text-n-slate-12">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.THRESHOLD_MESSAGES') }}
            </label>
            <input
              v-model="rule_outgoing_threshold"
              type="number"
              class="reset-base w-20 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong focus:outline-n-blue-9 px-3 py-1.5 text-sm bg-n-surface-1 text-n-slate-12"
            />
          </div>
          <div>
            <p class="text-sm text-n-slate-12 pb-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.SKIP_LABELS') }}
            </p>
            <TagMultiSelectComboBox
              v-model="rule_outgoing_skip_labels"
              :options="labelMenuItems"
              :placeholder="
                $t('INBOX_MGMT.SLACK_ALERTS.SELECT_LABELS_PLACEHOLDER')
              "
            />
            <p class="text-xs text-n-slate-11 mt-1">
              {{ $t('INBOX_MGMT.SLACK_ALERTS.SKIP_LABELS_HELP') }}
            </p>
          </div>
          <SettingsToggleSection
            v-model="rule_outgoing_repeat"
            :header="$t('INBOX_MGMT.SLACK_ALERTS.REPEAT_LABEL')"
          />
          <SettingsToggleSection
            v-model="rule_outgoing_apply_ai"
            :header="$t('INBOX_MGMT.SLACK_ALERTS.APPLY_AI_LABEL')"
          >
            <template v-if="rule_outgoing_apply_ai" #editor>
              <div class="flex flex-col gap-1">
                <p class="text-sm text-n-slate-12">
                  {{ $t('INBOX_MGMT.SLACK_ALERTS.AI_PROMPT_FOR_OUTGOING') }}
                </p>
                <textarea
                  v-model="rule_outgoing_ai_prompt"
                  rows="4"
                  class="w-full rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong focus:outline-n-blue-9 p-3 text-sm bg-n-surface-1 text-n-slate-12 resize-none"
                  :placeholder="
                    $t('INBOX_MGMT.SLACK_ALERTS.AI_PROMPT_OUTGOING_PLACEHOLDER')
                  "
                  spellcheck="false"
                />
              </div>
            </template>
          </SettingsToggleSection>
        </div>
      </template>
    </SettingsToggleSection>

    <!-- MAX DAYS UNRESOLVED -->
    <SettingsToggleSection
      v-if="slack_alerts_enabled"
      v-model="rule_days_enabled"
      :header="$t('INBOX_MGMT.SLACK_ALERTS.RULE_MAX_DAYS_UNRESOLVED')"
      :description="$t('INBOX_MGMT.SLACK_ALERTS.DAYS_UNRESOLVED_DESC')"
    >
      <template v-if="rule_days_enabled" #editor>
        <div class="flex items-center gap-4">
          <label class="flex-1 text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.SLACK_ALERTS.THRESHOLD_DAYS') }}
          </label>
          <input
            v-model="rule_days_threshold"
            type="number"
            class="reset-base w-20 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong focus:outline-n-blue-9 px-3 py-1.5 text-sm bg-n-surface-1 text-n-slate-12"
          />
        </div>
      </template>
    </SettingsToggleSection>

    <div class="pt-2">
      <NextButton
        :disabled="isSaving"
        :is-loading="isSaving"
        @click="updateSlackAlerts"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.UPDATE') }}
      </NextButton>
    </div>
  </div>
</template>
